<template>
    <div class="login-modern-page">
        <loadingScreen :isLoading="isLoading"/>
        <div class="login-modern-shell" :class="{'has-season': seasonActive}">
            <section class="login-hero-panel">
                <div class="login-hero-glow login-hero-glow-one"></div>
                <div class="login-hero-glow login-hero-glow-two"></div>
                <!-- Festas: lua, neve, trenó, enfeites com presente escondido; no Réveillon, fogos -->
                <season-login-scene v-if="seasonActive"></season-login-scene>

                <div class="login-hero-top">
                    <div class="login-brand-chip">
                        <span class="login-brand-dot"></span>
                        R2 Disponibilidade
                    </div>
                    <div v-if="seasonActive && seasonCountdownInfo" class="login-season-chip" :class="{'is-new-year': seasonNewYear}"
                         data-cy="login_season_countdown">
                        <svg viewBox="-12 -12 24 24" aria-hidden="true"><path :d="seasonStarPath"/></svg>
                        <span>{{ seasonCountdownInfo.text }}</span>
                    </div>
                </div>

                <div class="login-hero-content">
                    <p class="login-kicker">Gestao imobiliaria inteligente</p>
                    <h1>Controle reservas, propostas e disponibilidade em um unico lugar.</h1>
                    <p class="login-hero-description">
                        Acompanhe lotes, clientes, corretores e etapas comerciais com clareza para tomar decisoes mais rapidas.
                    </p>

                    <div class="login-feature-grid">
                        <div class="login-feature-card">
                            <span class="login-feature-icon icon-map"></span>
                            <strong>Lotes</strong>
                            <small>Disponibilidade sempre visivel</small>
                        </div>
                        <div class="login-feature-card">
                            <span class="login-feature-icon icon-event_available"></span>
                            <strong>Reservas</strong>
                            <small>Fluxos comerciais organizados</small>
                        </div>
                        <div class="login-feature-card">
                            <span class="login-feature-icon icon-people"></span>
                            <strong>Clientes</strong>
                            <small>Cadastro e relacionamento</small>
                        </div>
                    </div>

                    <router-link :to="{ name: 'Presentation' }" class="login-more-link"
                                 data-cy="login_presentation_link">
                        Saiba mais sobre o R2 Disponibilidade
                        <i class="fas fa-arrow-right"></i>
                    </router-link>
                </div>

                <div class="login-hero-footer">
                    <div>
                        <strong>R2 Soft</strong>
                        <span>Plataforma para loteamentos e equipes comerciais</span>
                    </div>
                </div>
            </section>

            <section class="login-form-panel">
                <form @submit.prevent="login" class="login-modern-form">
                    <div class="login-form-header text-center">
                        <a href="#" class="login-logo" aria-label="R2 Soft">
                            <img v-if="seasonHat" class="login-logo-season" :src="logoHat" alt="R2 Soft"
                                 @click.prevent="logoBurst">
                            <img v-else src="@/assets/img/logo.png" alt="R2 Soft" @click.prevent="logoBurst">
                        </a>
                        <span class="login-form-eyebrow">Acesso seguro<span v-if="seasonActive"
                              class="login-form-eyebrow-season">&nbsp;·&nbsp;{{ seasonGreetingText }}</span></span>
                        <h2>{{ t('GENERAL.WELCOME') }}</h2>
                        <p>{{ t('GENERAL.MSG.PLEASE_LOGIN') }}</p>
                    </div>

                    <div class="form-group login-field-group">
                        <label for="username">E-mail</label>
                        <div class="login-input-wrap">
                            <span class="login-input-icon icon-mail"></span>
                            <input data-cy="login_usuario_input" type="text" id="username" v-model="username"
                                   name="username"
                                   class="form-control" @keyup="inputEmail" placeholder="Endereço de e-mail"/>
                        </div>
                        <div class="validation" v-if="emailInvalid">{{ t('GENERAL.MSG.REQUIRED_FIELD') }}</div>
                        <div class="validation" v-if="emailFormInvalid">{{ t('GENERAL.MSG.EMAIL_INVALID') }}</div>
                    </div>

                    <div class="form-group login-field-group">
                        <label for="password">Senha</label>
                        <div class="login-input-wrap">
                            <span class="login-input-icon icon-lock"></span>
                            <input data-cy="login_password_input" type="password" id="password" v-model="password"
                                   name="password" class="form-control" @keyup="inputPassword" placeholder="Senha"/>
                        </div>
                        <div class="validation" v-if="passwordInvalid">{{ t('GENERAL.MSG.REQUIRED_FIELD') }}</div>
                    </div>

                    <div class="login-form-actions-row">
                        <div data-cy="login_password_recover" class="forgot-pwd">
                            <div @click="forgetPassword()" class="text-primary font-weight-bold pointer">
                                {{ t('GENERAL.MSG.FORGET_PASSWORD') }}
                            </div>
                        </div>
                    </div>

                    <div v-if="turnstileEnabled" class="login-turnstile-wrapper">
                        <div ref="turnstileContainer" data-cy="login_turnstile_widget"></div>
                        <small v-if="turnstileMessage" class="text-muted text-center d-block mt-2">
                            {{ turnstileMessage }}
                        </small>
                        <button type="button" class="btn btn-outline-primary btn-sm login-turnstile-reload"
                                @click="reloadTurnstileWidget" data-cy="login_turnstile_reload">
                            Recarregar captcha
                        </button>
                    </div>

                    <button data-cy="login_login_button" type="submit" class="btn btn-primary login-submit-button" :disabled="isLoading">
                        <span v-if="isLoading" class="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
                        <span v-if="!isLoading">{{ t('GENERAL.LOGIN') }}</span>
                        <span v-else>{{ t('GENERAL.MSG.LOGGING_IN') }}</span>
                    </button>

                    <div v-if="!toFinalUser && notNow" class="login-create-account">
                        <span>{{ t('GENERAL.NEW') }}?</span>
                        <router-link data-cy="login_creat_account_button" class="btn btn-dark" to="/singup">
                            {{ t('GENERAL.CREATE_ACCOUNT') }}
                        </router-link>
                    </div>
                </form>
            </section>
        </div>
    </div>
</template>

<script>

import {mapGetters} from 'vuex';
import User from '@/services/user';
import {useI18n} from "vue-i18n";
import {useToast} from "vue-toastification";
import Validate from '@/components/validate.vue';
import LoadingScreen from '@/components/layouts/loadScreenComponent.vue';
import {mapState} from 'vuex';
import SeasonLoginScene from '@/pages/login/partials/SeasonLoginScene.vue';
import seasonBurst from '@/Helpers/global/seasonBurst';
import {seasonCountdown, seasonGreeting} from '@/Helpers/global/season';
import {SPARKLE_PATH, STAR_PATH} from '@/Helpers/global/seasonShapes';
import logoHat from '@/assets/img/logo_com_gorro.png';

export default {
    name: 'LoginComponent',
    mixins: [Validate],
    setup() {
        const toast = useToast();
        const {t} = useI18n();
        return {
            t,
            toast
        }
    },

    components: {
        LoadingScreen,
        SeasonLoginScene,
    },

    created() {
        document.title = this.t('APP.TITLE.SYSTEM') + this.t('APP.TITLE.LOGIN');
        if (process.env.VUE_APP_HIDE_CREATE_USER === 'true') {
            this.toFinalUser = true;
        }
    },

    mounted() {
        this.loadTurnstileConfig();
    },

    beforeUnmount() {
        this.removeTurnstileWidget();
    },

    data() {
        return {
            user: null,
            resp: null,
            invalid: [],
            username: null,
            password: null,
            isLoading: false,
            toFinalUser: false,
            emailInvalid: false,
            passwordInvalid: false,
            emailFormInvalid: false,
            turnstileEnabled: false,
            turnstileSiteKey: null,
            turnstileAction: 'login',
            turnstileToken: null,
            turnstileWidgetId: null,
            turnstileMessage: null,
            turnstileRetryCount: 0,
            turnstileRetryTimeout: null,
            logoHat,
        }
    },

    computed: {
        ...mapGetters['user'],
        ...mapState(['seasonActive', 'seasonNewYear', 'seasonNow']),
        // Logo com gorro só no Natal
        seasonHat() {
            return this.seasonActive && !this.seasonNewYear;
        },
        seasonGreetingText() {
            return seasonGreeting(this.seasonNow);
        },
        seasonCountdownInfo() {
            return seasonCountdown(this.seasonNow);
        },
        seasonStarPath() {
            return this.seasonNewYear ? SPARKLE_PATH : STAR_PATH;
        },
    },

    methods: {
        // Easter egg da logo nas festas: balança e solta neve do gorro (ou confete no Réveillon)
        logoBurst(event) {
            if (this.seasonActive) seasonBurst(event.currentTarget, {newYear: this.seasonNewYear, sway: true});
        },
        forgetPassword()
        {
            window.location.href = `${process.env.VUE_APP_URL_AUTH_FRONT}/recuperar-senha`;
        },
        loadTurnstileConfig() {
            User.getTurnstileConfig().then(resp => {
                const config = resp.data || {};

                if (!config.enabled || !config.siteKey) {
                    return;
                }

                this.turnstileEnabled = true;
                this.turnstileSiteKey = config.siteKey;
                this.turnstileAction = config.action || 'login';

                this.$nextTick(() => {
                    this.loadTurnstileScript().then(() => {
                        this.renderTurnstileWidget();
                    }).catch(() => {
                        this.toast.error('Nao foi possivel carregar a verificacao de seguranca. Tente novamente.');
                    });
                });
            }).catch(() => {
                this.turnstileEnabled = false;
            });
        },
        loadTurnstileScript(forceReload = false) {
            if (forceReload) {
                const script = document.getElementById('turnstile-api-script');

                if (script) {
                    script.remove();
                }

                window.turnstile = null;
                window.turnstileScriptLoading = null;
            }

            if (window.turnstile) {
                return Promise.resolve();
            }

            if (window.turnstileScriptLoading) {
                return window.turnstileScriptLoading;
            }

            window.turnstileScriptLoading = new Promise((resolve, reject) => {
                const script = document.createElement('script');
                script.id = 'turnstile-api-script';
                script.src = `https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit&retry=${Date.now()}`;
                script.async = true;
                script.defer = true;
                script.onload = () => resolve();
                script.onerror = () => {
                    window.turnstileScriptLoading = null;
                    reject();
                };
                document.head.appendChild(script);
            });

            return window.turnstileScriptLoading;
        },
        renderTurnstileWidget() {
            if (!window.turnstile || !this.$refs.turnstileContainer || this.turnstileWidgetId !== null) {
                return;
            }

            this.turnstileWidgetId = window.turnstile.render(this.$refs.turnstileContainer, {
                sitekey: this.turnstileSiteKey,
                action: this.turnstileAction || 'login',
                callback: token => {
                    this.clearTurnstileRetryTimeout();
                    this.turnstileMessage = null;
                    this.turnstileRetryCount = 0;
                    this.turnstileToken = token;
                },
                'expired-callback': () => {
                    this.turnstileToken = null;
                    this.turnstileMessage = 'A verificacao de seguranca expirou. Recarregue o captcha e tente novamente.';
                },
                'error-callback': errorCode => {
                    this.clearTurnstileRetryTimeout();
                    this.turnstileToken = null;
                    this.turnstileMessage = `Nao foi possivel validar a verificacao de seguranca${errorCode ? ` (${errorCode})` : ''}. Recarregue o captcha e tente novamente.`;
                    return true;
                },
                'timeout-callback': () => {
                    this.turnstileToken = null;
                    this.turnstileMessage = 'A verificacao de seguranca demorou mais que o esperado. Recarregando captcha...';
                    this.recreateTurnstileWidget(true);
                },
            });

            this.scheduleTurnstileRetry();
        },
        clearTurnstileRetryTimeout() {
            if (this.turnstileRetryTimeout) {
                clearTimeout(this.turnstileRetryTimeout);
                this.turnstileRetryTimeout = null;
            }
        },
        scheduleTurnstileRetry() {
            this.clearTurnstileRetryTimeout();
            this.turnstileRetryTimeout = setTimeout(() => {
                if (!this.turnstileEnabled || this.turnstileToken) {
                    return;
                }

                this.turnstileMessage = 'A verificacao de seguranca demorou mais que o esperado. Recarregando captcha...';
                this.recreateTurnstileWidget(true);
            }, 30000);
        },
        recreateTurnstileWidget(forceReloadScript = false) {
            if (this.turnstileRetryCount >= 2) {
                this.turnstileMessage = 'Nao foi possivel concluir a verificacao. Clique em Recarregar captcha ou limpe os dados do navegador para este site.';
                return;
            }

            this.turnstileRetryCount += 1;
            this.removeTurnstileWidget();

            this.$nextTick(() => {
                this.loadTurnstileScript(forceReloadScript).then(() => {
                    this.renderTurnstileWidget();
                }).catch(() => {
                    this.toast.error('Nao foi possivel carregar a verificacao de seguranca. Tente novamente.');
                });
            });
        },
        resetTurnstileWidget() {
            this.turnstileToken = null;

            if (window.turnstile && this.turnstileWidgetId !== null) {
                window.turnstile.reset(this.turnstileWidgetId);
                this.scheduleTurnstileRetry();
            }
        },
        reloadTurnstileWidget() {
            this.turnstileRetryCount = 0;
            this.turnstileMessage = null;
            this.recreateTurnstileWidget(true);
        },
        removeTurnstileWidget() {
            this.clearTurnstileRetryTimeout();

            if (window.turnstile && this.turnstileWidgetId !== null && window.turnstile.remove) {
                window.turnstile.remove(this.turnstileWidgetId);
            }

            this.turnstileWidgetId = null;
            this.turnstileToken = null;
        },
        login() {
            if (this.checkForm()) {
                if (this.turnstileEnabled && !this.turnstileToken) {
                    this.toast.error('Resolva a verificacao de seguranca para continuar.');
                    return;
                }

                this.isLoading = true;
                this.user = {
                    password: this.password,
                    username: this.username,
                    grant_type: process.env.VUE_APP_GRANT_TYPE_PASSWORD,
                    withCompanies: true,
                    systemId: process.env.VUE_APP_SYSTEM_ID,
                }

                if (this.turnstileEnabled) {
                    this.user.turnstile_token = this.turnstileToken;
                    this.user.turnstile_action = this.turnstileAction || 'login';
                }

                User.authUser(this.user).then(resp => {
                    this.$store.dispatch('ActionDoLogin', resp.data);
                    this.$router.push({name: 'Home'});
                }).catch(error => {
                    this.isLoading = false;
                    this.resetTurnstileWidget();

                    if (error.response === undefined) {
                        this.toast.error('Sistema temporariamente indisponível, por favor tente mais tarde!');
                    } else {
                        this.error(error);
                    }
                });
            }
        },
        checkForm() {
            this.startValidation();
            this.emailValidate();
            this.passwordValidade();
            if (this.invalid.length && this.invalid.length > 0) {
                return false;
            }
            return true;
        },

        startValidation() {
            this.invalid = [];
            this.emailInvalid = false,
                    this.passwordInvalid = false
        },
        validEmail(email) {
            var re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
            return re.test(email);
        },
        emailValidate() {
            if (!this.username) {
                this.emailInvalid = true;
                this.invalid.push(this.emailInvalid);
                document.getElementById('username').classList.add('is-invalid');
            } else if (!this.validEmail(this.username)) {
                this.emailFormInvalid = true;
                this.invalid.push(this.emailFormInvalid);
                document.getElementById('username').classList.add('is-invalid');
            } else {
                document.getElementById('username').classList.add('is-valid');
            }
            if (this.username) {
                this.username = this.username.trim();
                this.username = this.username.toLowerCase();
            }
        },
        passwordValidade() {
            if (!this.password) {
                this.passwordInvalid = true;
                this.invalid.push(this.passwordInvalid);
                document.getElementById('password').classList.add('is-invalid');
            } else {
                document.getElementById('password').classList.add('is-valid');
            }
            if (this.password) {
                this.password = this.password.trim();
            }
        },
        inputEmail() {
            this.emailInvalid = false;
            this.emailFormInvalid = false;
            document.getElementById('username').classList.remove('is-valid');
            document.getElementById('username').classList.remove('is-invalid');
            this.emailValidate();
        },
        inputPassword() {
            this.passwordInvalid = false;
            document.getElementById('password').classList.remove('is-valid');
            document.getElementById('password').classList.remove('is-invalid');
            this.passwordValidade();
        },
        async handleClickSignIn() {
            this.isLoading = true;
            try {
                const googleUser = await this.$gAuth.signIn();
                if (!googleUser)
                    return null;
                this.onSignInGoogle(googleUser.getAuthResponse())
            } catch (error) {
                this.error(error);
                return null;
            }
        },
        onSignInGoogle(googleUser) {
            User.google(googleUser).then(resp => {
                this.$store.dispatch('ActionDoLogin', resp.data);
                this.isLoading = false;
                this.$router.push({name: 'Home'});
            });
        },
        error(error) {
            this.errorMsg(error);
            this.isLoading = false;
            document.getElementById('username').classList.remove('is-valid');
            document.getElementById('username').classList.add('is-invalid');
            document.getElementById('password').classList.remove('is-valid');
            document.getElementById('password').classList.add('is-invalid');
        },
    }
}
</script>
<style lang="css">
.fade-enter-active,
.fade-leave-active {
    transition: opacity .5s;
}

.fade-enter,
.fade-leave-to {
    opacity: 0;
}

.fa-google {
    background: conic-gradient(from -45deg, #ea4335 110deg, #4285f4 90deg 180deg, #34a853 180deg 270deg, #fbbc05 270deg) 73% 55%/150% 150% no-repeat;
    -webkit-background-clip: text;
    background-clip: text;
    color: transparent;
    -webkit-text-fill-color: transparent;
    font-size: 17px;
}

.login-modern-page {
    --login-bg: #eef5f3;
    --login-bg-rgb: 238, 245, 243;
    --login-panel: rgba(255, 255, 255, 0.86);
    --login-panel-strong: #ffffff;
    --login-border: rgba(15, 94, 78, 0.14);
    --login-text: #0f172a;
    --login-text-soft: #475569;
    --login-text-muted: #64748b;
    --login-primary: #0e5e4e;
    --login-primary-hover: #0b4c40;
    --login-primary-soft: rgba(14, 94, 78, 0.12);
    --login-accent: #14b8a6;
    --login-shadow: 0 28px 80px rgba(15, 23, 42, 0.16);
    min-height: 100vh;
    width: 100%;
    min-width: 100vw;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 32px;
    background:
        radial-gradient(circle at 12% 18%, rgba(20, 184, 166, 0.22), transparent 30%),
        radial-gradient(circle at 88% 8%, rgba(14, 94, 78, 0.20), transparent 28%),
        linear-gradient(135deg, #f8fafc 0%, var(--login-bg) 46%, #e5f2ef 100%);
    color: var(--login-text);
    overflow-x: hidden;
    overflow-y: auto;
    position: fixed;
    inset: 0;
}

.login-modern-page:before {
    content: "";
    position: absolute;
    inset: 0;
    background-image:
        linear-gradient(rgba(14, 94, 78, 0.06) 1px, transparent 1px),
        linear-gradient(90deg, rgba(14, 94, 78, 0.06) 1px, transparent 1px);
    background-size: 52px 52px;
    mask-image: linear-gradient(to bottom, rgba(0, 0, 0, 0.7), transparent 78%);
    pointer-events: none;
}

.login-modern-shell {
    width: min(1120px, 100%);
    min-height: 680px;
    display: grid;
    grid-template-columns: minmax(0, 1.12fr) minmax(390px, 0.88fr);
    border: 1px solid var(--login-border);
    border-radius: 36px;
    background: rgba(var(--login-bg-rgb), 0.58);
    box-shadow: var(--login-shadow);
    overflow: hidden;
    position: relative;
    z-index: 1;
    backdrop-filter: blur(22px) saturate(120%);
}

.login-hero-panel {
    min-height: 680px;
    padding: 42px;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    background:
        radial-gradient(circle at 74% 18%, rgba(94, 234, 212, 0.28), transparent 32%),
        radial-gradient(circle at 10% 78%, rgba(255, 255, 255, 0.14), transparent 30%),
        linear-gradient(145deg, rgba(14, 94, 78, 0.96), rgba(6, 78, 59, 0.90));
    color: #ffffff;
    position: relative;
    overflow: hidden;
}

.login-hero-panel:after {
    content: "";
    position: absolute;
    inset: auto -20% -34% 14%;
    height: 360px;
    background: rgba(255, 255, 255, 0.10);
    border-radius: 999px;
    transform: rotate(-12deg);
}

.login-hero-glow {
    position: absolute;
    width: 260px;
    height: 260px;
    border-radius: 50%;
    filter: blur(8px);
    opacity: 0.75;
}

.login-hero-glow-one {
    right: -80px;
    top: -70px;
    background: radial-gradient(circle, rgba(94, 234, 212, 0.65), transparent 68%);
}

.login-hero-glow-two {
    left: -120px;
    bottom: 80px;
    background: radial-gradient(circle, rgba(255, 255, 255, 0.28), transparent 68%);
}

.login-brand-chip,
.login-hero-content,
.login-hero-footer {
    position: relative;
    z-index: 1;
}

.login-brand-chip {
    width: fit-content;
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 14px;
    border: 1px solid rgba(255, 255, 255, 0.22);
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.12);
    color: rgba(255, 255, 255, 0.88);
    font-size: 13px;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
}

.login-brand-dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: #5eead4;
    box-shadow: 0 0 0 7px rgba(94, 234, 212, 0.18);
}

.login-kicker {
    margin-bottom: 14px;
    color: rgba(255, 255, 255, 0.78) !important;
    font-size: 13px;
    font-weight: 800;
    letter-spacing: 0.11em;
    text-transform: uppercase;
}

.login-hero-content h1 {
    max-width: 560px;
    margin: 0;
    color: #ffffff !important;
    font-size: clamp(34px, 4vw, 56px);
    line-height: 0.98;
    letter-spacing: -0.055em;
    font-weight: 800;
}

.login-hero-description {
    max-width: 500px;
    margin: 22px 0 0;
    color: rgba(255, 255, 255, 0.76) !important;
    font-size: 16px;
    line-height: 1.7;
}

.login-feature-grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 12px;
    margin-top: 38px;
}

.login-feature-card {
    min-height: 132px;
    padding: 18px;
    border: 1px solid rgba(255, 255, 255, 0.16);
    border-radius: 22px;
    background: rgba(255, 255, 255, 0.10);
    backdrop-filter: blur(10px);
}

.login-feature-icon {
    width: 38px;
    height: 38px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 18px;
    border-radius: 14px;
    background: rgba(255, 255, 255, 0.16);
    color: #5eead4;
    font-size: 18px;
}

.login-feature-card strong,
.login-feature-card small {
    display: block;
}

.login-feature-card strong {
    color: #ffffff;
    font-size: 15px;
}

.login-feature-card small {
    margin-top: 5px;
    color: rgba(255, 255, 255, 0.66);
    line-height: 1.35;
}

.login-more-link {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    margin-top: 18px;
    padding: 11px 18px;
    border: 1px solid rgba(255, 255, 255, 0.22);
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.12);
    color: #ffffff !important;
    font-size: 14px;
    font-weight: 700;
    text-decoration: none !important;
    transition: background 0.2s, border-color 0.2s;
}

.login-more-link i {
    color: #5eead4;
    font-size: 12px;
    transition: transform 0.2s;
}

.login-more-link:hover {
    border-color: rgba(94, 234, 212, 0.55);
    background: rgba(255, 255, 255, 0.18);
}

.login-more-link:hover i {
    transform: translateX(3px);
}

.login-hero-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding-top: 28px;
    color: rgba(255, 255, 255, 0.74);
}

.login-hero-footer strong,
.login-hero-footer span {
    display: block;
}

.login-hero-footer strong {
    color: #ffffff;
    font-size: 15px;
}

.login-hero-footer span {
    margin-top: 4px;
    font-size: 13px;
}

.login-form-panel {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 42px;
    background: var(--login-panel);
}

.login-modern-form {
    width: 100%;
    max-width: 410px;
}

.login-form-header {
    margin-bottom: 34px;
}

.login-logo {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 22px;
}

.login-logo img {
    max-width: 178px;
    height: auto;
    margin: auto;
}

.login-form-eyebrow {
    display: inline-flex;
    padding: 7px 12px;
    border-radius: 999px;
    background: var(--login-primary-soft);
    color: var(--login-primary);
    font-size: 12px;
    font-weight: 800;
    letter-spacing: 0.08em;
    text-transform: uppercase;
}

.login-form-header h2 {
    margin: 18px 0 8px;
    color: var(--login-text) !important;
    font-size: 32px;
    font-weight: 800;
    letter-spacing: -0.035em;
}

.login-form-header p {
    margin: 0;
    color: var(--login-text-muted) !important;
    font-size: 15px;
}

.login-field-group {
    margin-bottom: 18px;
}

.login-field-group label {
    margin-bottom: 8px;
    color: var(--login-text-soft) !important;
    font-size: 13px;
    font-weight: 800;
}

.login-input-wrap {
    position: relative;
}

.login-input-icon {
    position: absolute;
    left: 16px;
    top: 50%;
    transform: translateY(-50%);
    color: var(--login-text-muted);
    font-size: 17px;
    z-index: 2;
}

.login-input-wrap .form-control {
    height: 54px;
    padding-left: 48px;
    border: 1px solid var(--login-border);
    border-radius: 17px;
    background: var(--login-panel-strong);
    color: var(--login-text);
    box-shadow: none;
    transition: border-color .2s ease, box-shadow .2s ease, background-color .2s ease;
}

.login-input-wrap .form-control::placeholder {
    color: var(--login-text-muted);
}

.login-input-wrap .form-control:focus {
    border-color: rgba(14, 94, 78, 0.55);
    box-shadow: 0 0 0 4px var(--login-primary-soft);
}

.login-form-actions-row {
    display: flex;
    justify-content: flex-end;
    margin: 4px 0 22px;
}

.login-form-actions-row .forgot-pwd a {
    color: var(--login-primary) !important;
    font-size: 13px;
    font-weight: 800;
}

.login-form-actions-row .forgot-pwd a:hover {
    color: var(--login-primary-hover) !important;
}

.login-turnstile-wrapper {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    margin: 0 0 22px;
}

.login-turnstile-reload {
    margin-top: 8px;
    border-color: var(--login-primary);
    color: var(--login-primary) !important;
    font-weight: 700;
}

.login-turnstile-reload:hover,
.login-turnstile-reload:focus {
    background: var(--login-primary) !important;
    color: #ffffff !important;
}

.login-submit-button {
    width: 100%;
    min-height: 54px;
    border: none;
    border-radius: 17px;
    background: linear-gradient(135deg, var(--login-primary), var(--login-accent)) !important;
    box-shadow: 0 18px 34px rgba(14, 94, 78, 0.28);
    font-weight: 800;
}

.login-submit-button:hover,
.login-submit-button:focus {
    background: linear-gradient(135deg, var(--login-primary-hover), var(--login-primary)) !important;
    box-shadow: 0 20px 38px rgba(14, 94, 78, 0.34);
}

.login-create-account {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 14px;
    margin-top: 26px;
    padding: 16px;
    border: 1px solid var(--login-border);
    border-radius: 20px;
    background: rgba(var(--login-bg-rgb), 0.60);
}

.login-create-account span {
    color: var(--login-text-muted);
    font-weight: 700;
}

.login-create-account .btn-dark {
    border: 1px solid var(--login-border);
    border-radius: 14px;
    background: var(--login-text) !important;
    color: var(--login-panel-strong) !important;
    font-weight: 800;
}

.login-modern-page .validation {
    margin-top: 7px;
    text-align: left;
}

@media (max-width: 991.98px) {
    .login-modern-page {
        padding: 22px;
        align-items: flex-start;
    }

    .login-modern-shell {
        min-height: auto;
        grid-template-columns: 1fr;
        border-radius: 28px;
    }

    .login-hero-panel {
        min-height: auto;
        padding: 34px;
    }

    .login-feature-grid {
        grid-template-columns: 1fr;
    }

    .login-form-panel {
        padding: 34px;
    }
}

@media (max-width: 575.98px) {
    .login-modern-page {
        padding: 0;
    }

    .login-modern-shell {
        min-height: 100vh;
        border: 0;
        border-radius: 0;
    }

    .login-hero-panel {
        padding: 28px 22px;
    }

    .login-hero-content h1 {
        font-size: 32px;
    }

    .login-form-panel {
        padding: 30px 20px 34px;
    }

    .login-create-account {
        align-items: stretch;
        flex-direction: column;
        text-align: center;
    }

    .login-create-account .btn-dark {
        width: 100%;
    }
}
</style>
